import React from "react";
import {
  FiCalendar,
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiImage,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import EventCategoryBadge from "./EventCategoryBadge";
import EventStatusBadge from "./EventStatusBadge";

import { formatDateFr, formatDateTimeFr } from "../utils/eventsAdminUtils";

export default function EventsPreviewDrawer({
  open,
  item,
  onClose,
  onEditFromPreview,
}) {
  const imageUrl = item?.photo_vignette
    ? toAbsolutePublicUrl(item.photo_vignette)
    : "";

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title="Aperçu de l’événement"
      subtitle="Visualisez rapidement le rendu de l’événement."
      icon={FiEye}
      size="xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Fermer
          </AdminButton>

          {item?.id ? (
            <AdminButton
              icon={FiEdit2}
              variant="primary"
              onClick={onEditFromPreview}
            >
              Modifier cet événement
            </AdminButton>
          ) : null}
        </div>
      }
    >
      {!item ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Événement introuvable.
        </p>
      ) : (
        <article className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={item.title}
              className="max-h-[420px] w-full object-cover"
            />
          ) : (
            <div className="flex min-h-[240px] flex-col items-center justify-center bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
              <FiImage className="text-5xl" />
              <p className="mt-3 text-sm font-bold">Aucune vignette associée</p>
            </div>
          )}

          <div className="p-5 sm:p-7">
            <div className="flex flex-wrap items-center gap-3">
              <EventStatusBadge active={!!item.is_active} />
              <EventCategoryBadge label={item.categorie} />
            </div>

            <h1 className="mt-5 text-3xl font-bold leading-tight text-slate-950 dark:text-white">
              {item.title}
            </h1>

            {item.subtitle ? (
              <p className="mt-2 text-lg font-bold text-sky-700 dark:text-sky-300">
                {item.subtitle}
              </p>
            ) : null}

            <div className="mt-5 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 text-sm leading-7 text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
              <p className="flex items-center gap-2">
                <FiCalendar />
                <span>
                  <span className="font-bold text-slate-950 dark:text-white">
                    Début :
                  </span>{" "}
                  {item.all_day
                    ? formatDateFr(item.start_date)
                    : formatDateTimeFr(item.start_date)}
                </span>
              </p>

              {item.end_date ? (
                <p className="mt-1 flex items-center gap-2">
                  <FiCalendar />
                  <span>
                    <span className="font-bold text-slate-950 dark:text-white">
                      Fin :
                    </span>{" "}
                    {item.all_day
                      ? formatDateFr(item.end_date)
                      : formatDateTimeFr(item.end_date)}
                  </span>
                </p>
              ) : null}

              {item.all_day ? (
                <p className="mt-3 inline-flex rounded-full bg-sky-50 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
                  Événement sans horaire précis
                </p>
              ) : null}

              {item.external_link ? (
                <p className="mt-3">
                  <a
                    href={item.external_link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 font-bold text-sky-700 underline underline-offset-4 dark:text-sky-300"
                  >
                    <FiExternalLink />
                    Ouvrir le lien externe
                  </a>
                </p>
              ) : null}
            </div>

            {item.description ? (
              <div
                className="prose prose-slate mt-6 max-w-none dark:prose-invert prose-headings:font-bold prose-a:text-sky-600"
                dangerouslySetInnerHTML={{
                  __html: item.description,
                }}
              />
            ) : (
              <p className="mt-6 text-sm text-slate-500 dark:text-slate-400">
                Aucune description renseignée.
              </p>
            )}
          </div>
        </article>
      )}
    </AdminDrawer>
  );
}
